import { type CheckoutConfirmation as Confirmation } from 'hooks/billing/useConversionPopup';
import { usePricingConfig } from 'hooks/billing/usePricingConfig';
import { Check, X } from 'lucide-react';
import { DialogClose, DialogDescription, DialogTitle } from 'ui/dialog';
import { spawn } from 'utils/translations';

const spawnConfirm = spawn('billing.popup.confirmation');

interface CheckoutConfirmationProps {
  confirmation: Confirmation;
}

/**
 * Post-checkout confirmation view (PLT-3715, folded-in PLT-4100).
 *
 * Minimal success state: shows the plan name and credits granted. The billing
 * read-models are reloaded underneath (usePostCheckoutReturn) so the page
 * reflects the new plan immediately.
 */
export const CheckoutConfirmation = ({
  confirmation,
}: CheckoutConfirmationProps) => {
  const cfg = usePricingConfig();
  const plan = cfg.plans.find(
    (candidate) => candidate.sku === confirmation.sku,
  );
  const credits = plan?.credits.toLocaleString() ?? '';

  return (
    <div className="relative flex flex-col gap-3 p-6">
      <DialogClose className="absolute right-4 top-4 text-text-panel-neutral-mid transition-colors hover:text-text-panel-neutral-strong">
        <X className="size-4" />
      </DialogClose>
      <DialogTitle className="flex items-center gap-2 font-sans text-xl font-medium tracking-normal text-text-panel-neutral-strong">
        <Check className="size-5 text-icon-panel-positive-mid" />
        {spawnConfirm('title')}
      </DialogTitle>
      <DialogDescription className="text-sm text-text-panel-neutral-mid">
        {credits
          ? spawnConfirm('body', { credits })
          : spawnConfirm('body', { credits: confirmation.sku ?? '' })}
      </DialogDescription>
    </div>
  );
};
